import { Link } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import { ChevronDown, Download, Share, Plus } from "lucide-react";
import { useState } from "react";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { LaunchBundleBanner } from "./LaunchBundleBanner";

export const Header = () => {
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const { canInstall, isInstalled, isIOS, install } = usePwaInstall();

  const showInstallButton = !isInstalled && (canInstall || isIOS);

  const handleInstall = async () => {
    if (canInstall) {
      await install();
    } else if (isIOS) {
      setShowIosModal(true);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-[#0A192F]/95 backdrop-blur border-b border-[#C5A059]/20"
    >
      <LaunchBundleBanner />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <div className="w-9 h-9 border border-[#C5A059] flex items-center justify-center">
            <span className="font-display text-[#C5A059] text-lg">A</span>
          </div>
          <div className="text-white">
            <div className="font-display text-lg sm:text-xl leading-none">AIGL Channel</div>
            <div className="font-accent text-[9px] sm:text-[10px] text-[#C5A059] mt-0.5">
              Career Launch
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <a href="/#products" data-testid="nav-products" className="text-white/80 hover:text-[#C5A059] text-sm transition">
            Business Plans
          </a>
          <a href="/#bundle" data-testid="nav-bundle" className="text-[#C5A059] hover:text-[#D4AF37] text-sm font-semibold transition">
            Bundle &middot; Save 43%
          </a>
          <a href="/#why" data-testid="nav-why" className="text-white/80 hover:text-[#C5A059] text-sm transition">
            Why AIGL
          </a>
          <Link to="/about" data-testid="nav-about" className="text-white/80 hover:text-[#C5A059] text-sm transition">
            About Us
          </Link>
          <a href="/#testimonials" data-testid="nav-testimonials" className="text-white/80 hover:text-[#C5A059] text-sm transition">
            Reviews
          </a>
          <a href="/#faq" data-testid="nav-faq" className="text-white/80 hover:text-[#C5A059] text-sm transition">
            FAQ
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="currency-selector"
              onClick={() => setOpen((s) => !s)}
              className="flex items-center gap-1 text-white/90 border border-white/20 px-3 py-1.5 text-sm hover:border-[#C5A059]"
            >
              {currency}
              <ChevronDown className="w-3 h-3" />
            </button>
            {open && (
              <div
                data-testid="currency-dropdown"
                className="absolute right-0 mt-2 bg-white border border-slate-200 shadow-lg z-50"
              >
                {["USD", "GBP", "EUR"].map((c) => (
                  <button
                    key={c}
                    data-testid={`currency-option-${c}`}
                    onClick={() => {
                      setCurrency(c);
                      setOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${
                      currency === c ? "text-[#C5A059] font-semibold" : "text-slate-700"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
          {showInstallButton && (
            <button
              type="button"
              onClick={handleInstall}
              data-testid="header-install-app-btn"
              title="Install AIGL to your home screen"
              className="hidden md:inline-flex items-center gap-1.5 border border-[#C5A059]/50 hover:border-[#C5A059] hover:bg-[#C5A059]/10 text-[#C5A059] text-xs font-accent tracking-[1.5px] px-3 py-2 transition"
            >
              <Download className="w-3.5 h-3.5" />
              INSTALL
            </button>
          )}
          <a
            href="/#products"
            data-testid="header-cta"
            className="hidden sm:inline-block bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-bold text-xs sm:text-sm px-4 sm:px-5 py-2 sm:py-2.5 transition"
          >
            GET YOURS
          </a>
        </div>
      </div>

      {showIosModal && (
        <IosInstallModal onClose={() => setShowIosModal(false)} />
      )}
    </header>
  );
};

const IosInstallModal = ({ onClose }) => (
  <div
    data-testid="ios-install-modal"
    className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    onClick={onClose}
  >
    <div
      className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-[#0A192F] px-6 py-5 text-center">
        <div className="font-accent text-[#C5A059] text-[10px] tracking-[3px]">
          INSTALL AIGL CHANNEL
        </div>
        <h3 className="font-display text-2xl text-white mt-2">
          Add to your home screen
        </h3>
      </div>
      <ol className="p-6 space-y-4 text-sm text-slate-700">
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#C5A059] text-[#0A192F] font-bold flex items-center justify-center text-xs">
            1
          </span>
          <div>
            Tap the <Share className="inline w-4 h-4 mx-1 -mt-1 text-[#0A192F]" />
            <b>Share</b> icon in your browser's bottom bar
          </div>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#C5A059] text-[#0A192F] font-bold flex items-center justify-center text-xs">
            2
          </span>
          <div>
            Scroll and tap <Plus className="inline w-4 h-4 mx-1 -mt-1 text-[#0A192F]" />
            <b>Add to Home Screen</b>
          </div>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#C5A059] text-[#0A192F] font-bold flex items-center justify-center text-xs">
            3
          </span>
          <div>
            Tap <b>Add</b> in the top right corner — AIGL is now a tap away
            on your home screen
          </div>
        </li>
      </ol>
      <div className="bg-slate-50 px-6 py-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          data-testid="ios-install-modal-close"
          className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white text-xs font-accent tracking-[2px] px-5 py-2.5 transition"
        >
          GOT IT
        </button>
      </div>
    </div>
  </div>
);
